import {
  type Action,
  type UnknownAction,
  combineReducers,
  configureStore
} from '@reduxjs/toolkit';
import {
  type TypedUseSelectorHook,
  useDispatch,
  useSelector
} from 'react-redux';

import {
  ieLeasingOrderSlice,
  llpLeasingOrderSlice
} from '@/components/App/Leasing';
import { posCredApplicationSlice } from '@/components/App/PosCred/PosCredApclication/slice';
import { posCredSlice } from '@/components/App/PosCred/PosCreds/slice';
import { clientReducers } from '@/components/Client/client.store.ts';
import { wsMiddleware } from '@/integrations/socket.connector/socket.connector.ts';
import { adminSlice } from '@/store/admin/admin.slice';
import { adminDealersSlice } from '@/store/admin/adminDealers.slice';
import { adminModelsSlice } from '@/store/admin/adminModels.slice';
import { adminUsersSlice } from '@/store/admin/adminUsers.slice';

import { adminDictionarySlice } from './admin/adminDictionary.slice.ts';
import { adminExecutorSqlSlice } from './admin/executorSql.slice';
import { appSlice } from './app.slice';
import { authSlice } from './auth.slice';
import { bankSummarySlice } from './bankSummary.slice.ts';
import { clientsSlice } from './clients.slice';
import { dictSlice } from './dict.slice';
import { faqSlice } from './faq.slice.ts';
import { financingStepSlice } from './financingStep.slice';
import { leadsSlice } from './leads.slice.ts';
import { loadingSlice } from './loading.slice.ts';
import { newOrderSlice } from './newOrder.slice';
import { notificationsSlice } from './notifications.slice';
import { offerStepSlice } from './offerStep.slice';
import { orderInsuranceSlice } from './orderInsurance.slice';
import { orderStepSlice } from './orderStep.slice';
import { securitySlice } from './security.slice.ts';
import { serviceDeskSlice } from './serviceDesk.slice.ts';
import { templatesSlice } from './templates.slice';
import { userInfoSlice } from './userInfo.slice';

interface ResetAction {
  type: 'root/resetState';
}
type AppAction = ResetAction | Action;

const rootReducer = combineReducers({
  admin: adminSlice.reducer,
  adminDealers: adminDealersSlice.reducer,
  adminDictionary: adminDictionarySlice.reducer,
  adminExecutor: adminExecutorSqlSlice.reducer,
  adminModels: adminModelsSlice.reducer,
  adminUsers: adminUsersSlice.reducer,
  app: appSlice.reducer,
  auth: authSlice.reducer,
  bankSummary: bankSummarySlice.reducer,
  clients: clientsSlice.reducer,
  dict: dictSlice.reducer,
  faq: faqSlice.reducer,
  financingStep: financingStepSlice.reducer,
  ieLeasingOrder: ieLeasingOrderSlice.reducer,
  leads: leadsSlice.reducer,
  llpLeasingOrder: llpLeasingOrderSlice.reducer,
  loading: loadingSlice.reducer,
  newOrder: newOrderSlice.reducer,
  notifications: notificationsSlice.reducer,
  offerStep: offerStepSlice.reducer,
  orderInsurance: orderInsuranceSlice.reducer,
  orderStep: orderStepSlice.reducer,
  posCred: posCredSlice.reducer,
  posCredApplication: posCredApplicationSlice.reducer,
  security: securitySlice.reducer,
  serviceDesk: serviceDeskSlice.reducer,
  templates: templatesSlice.reducer,
  userInfo: userInfoSlice.reducer,
  ...clientReducers
});
const rootResetReducer = (
  state: ReturnType<typeof rootReducer> | undefined,
  action: AppAction
): ReturnType<typeof rootReducer> => {
  if (action.type === 'root/resetState') {
    // Сбрасываем состояние до начального, вызывая rootReducer с undefined
    return rootReducer(undefined, action as UnknownAction);
  }
  return rootReducer(state, action as UnknownAction);
};

const middleware = [wsMiddleware];

export const store = configureStore({
  reducer: rootResetReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(middleware)
});

export const dispatch = store.dispatch.bind(store);
export type TRootState = ReturnType<typeof store.getState>;
type TAppDispatch = typeof store.dispatch;

export const useAppDispatch: () => TAppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<TRootState> = useSelector;
